import React, { useState, Fragment } from 'react';
import { ArrowUpIcon, ArrowDownIcon, CheckIcon, XIcon, ClockIcon, TruckIcon, BarChart2Icon, ChevronRightIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { StatusBadge } from '../../StatusBadge';
export function PurchaseOrderSummary({
  filters,
  locale
}) {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedPO, setExpandedPO] = useState(null);
  // Sample data
  const purchaseOrders = [{
    id: 'PO-2023-001',
    name: 'Server Hardware',
    vendor: 'Server Solutions Inc.',
    status: 'Sent to Vendor',
    date: '2023-10-10',
    total: 12500.0,
    currency: 'USD',
    items: 5,
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'John Smith',
    approvalDate: '2023-10-09',
    expectedDelivery: '2023-11-05',
    lineItems: [{
      id: 'ITEM-001',
      description: 'Server Rack',
      quantity: 2,
      unitPrice: 1500,
      total: 3000
    }, {
      id: 'ITEM-002',
      description: 'Server Unit',
      quantity: 3,
      unitPrice: 3000,
      total: 9000
    }, {
      id: 'ITEM-003',
      description: 'Network Switch',
      quantity: 1,
      unitPrice: 500,
      total: 500
    }]
  }, {
    id: 'PO-2023-002',
    name: 'Marketing Materials',
    vendor: 'PrintWorks Inc.',
    status: 'Pending Confirmation',
    date: '2023-10-06',
    total: 3200.5,
    currency: 'USD',
    items: 3,
    entity: 'Global Technologies Inc.',
    site: 'San Francisco Office',
    approver: 'Jane Doe',
    approvalDate: '2023-10-05',
    expectedDelivery: '2023-10-30',
    lineItems: [{
      id: 'ITEM-004',
      description: 'Brochures',
      quantity: 1000,
      unitPrice: 1.5,
      total: 1500
    }, {
      id: 'ITEM-005',
      description: 'Banners',
      quantity: 5,
      unitPrice: 200,
      total: 1000
    }, {
      id: 'ITEM-006',
      description: 'Business Cards',
      quantity: 500,
      unitPrice: 0.75,
      total: 375
    }, {
      id: 'ITEM-007',
      description: 'Stickers',
      quantity: 1000,
      unitPrice: 0.25,
      total: 250
    }]
  }, {
    id: 'PO-2023-003',
    name: 'Office Supplies',
    vendor: 'Office Depot',
    status: 'Partially Delivered',
    date: '2023-09-15',
    total: 1850.75,
    currency: 'USD',
    items: 8,
    entity: 'Global Technologies Inc.',
    site: 'New York HQ',
    approver: 'Michael Johnson',
    approvalDate: '2023-09-14',
    expectedDelivery: '2023-09-30',
    lineItems: [{
      id: 'ITEM-008',
      description: 'Paper',
      quantity: 50,
      unitPrice: 10,
      total: 500
    }, {
      id: 'ITEM-009',
      description: 'Pens',
      quantity: 100,
      unitPrice: 2,
      total: 200
    }, {
      id: 'ITEM-010',
      description: 'Notebooks',
      quantity: 50,
      unitPrice: 5,
      total: 250
    }, {
      id: 'ITEM-011',
      description: 'Desk Organizers',
      quantity: 25,
      unitPrice: 15,
      total: 375
    }, {
      id: 'ITEM-012',
      description: 'Staplers',
      quantity: 15,
      unitPrice: 8,
      total: 120
    }]
  }, {
    id: 'PO-2023-004',
    name: 'IT Equipment',
    vendor: 'Dell Technologies',
    status: 'Delivered',
    date: '2023-09-05',
    total: 8750.0,
    currency: 'USD',
    items: 4,
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Sarah Williams',
    approvalDate: '2023-09-03',
    expectedDelivery: '2023-09-20',
    lineItems: [{
      id: 'ITEM-013',
      description: 'Laptops',
      quantity: 5,
      unitPrice: 1200,
      total: 6000
    }, {
      id: 'ITEM-014',
      description: 'Monitors',
      quantity: 5,
      unitPrice: 350,
      total: 1750
    }, {
      id: 'ITEM-015',
      description: 'Docking Stations',
      quantity: 5,
      unitPrice: 150,
      total: 750
    }, {
      id: 'ITEM-016',
      description: 'Wireless Keyboards',
      quantity: 5,
      unitPrice: 50,
      total: 250
    }]
  }, {
    id: 'PO-2023-005',
    name: 'Software Licenses',
    vendor: 'Microsoft',
    status: 'Completed',
    date: '2023-08-20',
    total: 5000.0,
    currency: 'USD',
    items: 2,
    entity: 'Global Technologies Inc.',
    site: 'All Sites',
    approver: 'John Smith',
    approvalDate: '2023-08-18',
    expectedDelivery: '2023-08-25',
    lineItems: [{
      id: 'ITEM-017',
      description: 'Office 365 Licenses',
      quantity: 50,
      unitPrice: 60,
      total: 3000
    }, {
      id: 'ITEM-018',
      description: 'Windows Server Licenses',
      quantity: 2,
      unitPrice: 1000,
      total: 2000
    }]
  }, {
    id: 'PO-2023-006',
    name: 'Office Furniture',
    vendor: 'IKEA Business',
    status: 'Cancelled',
    date: '2023-08-10',
    total: 4200.0,
    currency: 'EUR',
    items: 6,
    entity: 'European Operations Ltd',
    site: 'London Office',
    approver: 'Robert Brown',
    approvalDate: '2023-08-08',
    expectedDelivery: '2023-09-10',
    cancelledBy: 'Sarah Williams',
    cancellationDate: '2023-08-15',
    cancellationReason: 'Budget constraints',
    lineItems: [{
      id: 'ITEM-019',
      description: 'Desks',
      quantity: 5,
      unitPrice: 300,
      total: 1500
    }, {
      id: 'ITEM-020',
      description: 'Chairs',
      quantity: 10,
      unitPrice: 150,
      total: 1500
    }, {
      id: 'ITEM-021',
      description: 'Filing Cabinets',
      quantity: 3,
      unitPrice: 200,
      total: 600
    }, {
      id: 'ITEM-022',
      description: 'Bookshelves',
      quantity: 2,
      unitPrice: 150,
      total: 300
    }, {
      id: 'ITEM-023',
      description: 'Conference Table',
      quantity: 1,
      unitPrice: 300,
      total: 300
    }]
  }];
  // Filter purchase orders based on the provided filters
  const filteredPOs = purchaseOrders.filter(po => {
    const poDate = new Date(po.date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const dateInRange = (!startDate || poDate >= startDate) && (!endDate || poDate <= endDate);
    const matchesEntity = !filters.entity || po.entity.includes(filters.entity);
    const matchesSite = !filters.site || po.site.includes(filters.site);
    const matchesVendor = !filters.vendor || po.vendor.includes(filters.vendor);
    const matchesCurrency = !filters.currency || po.currency === filters.currency;
    const matchesStatus = !filters.status || po.status.toLowerCase().includes(filters.status.toLowerCase());
    return dateInRange && matchesEntity && matchesSite && matchesVendor && matchesCurrency && matchesStatus;
  });
  // Sort purchase orders
  const sortedPOs = [...filteredPOs].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'vendor':
        comparison = a.vendor.localeCompare(b.vendor);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'date':
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
      case 'total':
        comparison = a.total - b.total;
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Calculate summary statistics
  const totalPOs = filteredPOs.length;
  const totalValue = filteredPOs.reduce((sum, po) => sum + po.total, 0);
  const statusCounts = filteredPOs.reduce((counts, po) => {
    counts[po.status] = (counts[po.status] || 0) + 1;
    return counts;
  }, {});
  const vendorCounts = filteredPOs.reduce((counts, po) => {
    counts[po.vendor] = (counts[po.vendor] || 0) + 1;
    return counts;
  }, {});
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const togglePOExpansion = poId => {
    if (expandedPO === poId) {
      setExpandedPO(null);
    } else {
      setExpandedPO(poId);
    }
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'Sent to Vendor':
        return <StatusBadge status="info" text={status} data-id="element-14710" />;
      case 'Pending Confirmation':
        return <StatusBadge status="warning" text={status} data-id="element-14711" />;
      case 'Partially Delivered':
        return <StatusBadge status="info" text={status} data-id="element-14712" />;
      case 'Delivered':
        return <StatusBadge status="success" text={status} data-id="element-14713" />;
      case 'Completed':
        return <StatusBadge status="success" text={status} data-id="element-14714" />;
      case 'Cancelled':
        return <StatusBadge status="error" text={status} data-id="element-14715" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-14716" />;
    }
  };
  return <div className="p-6" data-id="element-14717">
      <div className="mb-8" data-id="element-14718">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-14719">
          Purchase Order Summary Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-14720">
          Analysis of purchase orders by status, vendor, and department
        </p>
      </div>
      {/* Summary cards */}
      <div className="p-4 sm:p-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-14721">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14722">
          <div className="p-5" data-id="element-14723">
            <div className="flex items-center" data-id="element-14724">
              <div className="flex-shrink-0" data-id="element-14725">
                <BarChart2Icon className="h-6 w-6 text-gray-400" data-id="element-14726" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14727">
                <dl data-id="element-14728">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14729">
                    Total Purchase Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-14730">
                    {totalPOs}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14731">
          <div className="p-5" data-id="element-14732">
            <div className="flex items-center" data-id="element-14733">
              <div className="flex-shrink-0" data-id="element-14734">
                <CheckIcon className="h-6 w-6 text-green-400" data-id="element-14735" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14736">
                <dl data-id="element-14737">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14738">
                    Completed Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-14739">
                    {statusCounts['Completed'] || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14740">
          <div className="p-5" data-id="element-14741">
            <div className="flex items-center" data-id="element-14742">
              <div className="flex-shrink-0" data-id="element-14743">
                <ClockIcon className="h-6 w-6 text-yellow-400" data-id="element-14744" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14745">
                <dl data-id="element-14746">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14747">
                    Pending Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-14748">
                    {(statusCounts['Pending Confirmation'] || 0) + (statusCounts['Sent to Vendor'] || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-14749">
          <div className="p-5" data-id="element-14750">
            <div className="flex items-center" data-id="element-14751">
              <div className="flex-shrink-0" data-id="element-14752">
                <TruckIcon className="h-6 w-6 text-indigo-400" data-id="element-14753" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14754">
                <dl data-id="element-14755">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14756">
                    Total Value
                  </dt>
                  <dd className="text-lg font-medium text-gray-900" data-id="element-14757">
                    {formatCurrency(totalValue, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Purchase Orders Table */}
      <div className="px-4 py-5 sm:p-6" data-id="element-14758">
        <div className="flex flex-col" data-id="element-14759">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-14760">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-14761">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-14762">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-14763">
                  <thead className="bg-gray-50" data-id="element-14764">
                    <tr data-id="element-14765">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14766">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('id')} data-id="element-14767">
                          <span data-id="element-14768">PO Number</span>
                          {sortField === 'id' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14769" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14770" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14771" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14772">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('name')} data-id="element-14773">
                          <span data-id="element-14774">Description</span>
                          {sortField === 'name' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14775" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14776" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14777" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14778">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('vendor')} data-id="element-14779">
                          <span data-id="element-14780">Vendor</span>
                          {sortField === 'vendor' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14781" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14782" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14783" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14784">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('status')} data-id="element-14785">
                          <span data-id="element-14786">Status</span>
                          {sortField === 'status' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14787" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14788" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14789" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14790">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('date')} data-id="element-14791">
                          <span data-id="element-14792">Date</span>
                          {sortField === 'date' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14793" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14794" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14795" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14796">
                        <button className="group flex items-center space-x-1 text-left focus:outline-none" onClick={() => handleSort('total')} data-id="element-14797">
                          <span data-id="element-14798">Total</span>
                          {sortField === 'total' ? sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 text-gray-500" data-id="element-14799" /> : <ArrowDownIcon className="h-4 w-4 text-gray-500" data-id="element-14800" /> : <ArrowUpIcon className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100" data-id="element-14801" />}
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14802">
                        <span data-id="element-14803">Items</span>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14804">
                        <span data-id="element-14805">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-14806">
                    {sortedPOs.map(po => <Fragment key={po.id} data-id="element-14807">
                        <tr className={expandedPO === po.id ? 'bg-indigo-50' : 'hover:bg-gray-50'} data-id="element-14808">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-14809">
                            {po.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-14810">
                            {po.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14811">
                            {po.vendor}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-14812">
                            {getStatusBadge(po.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14813">
                            {new Date(po.date).toLocaleDateString(locale)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-14814">
                            {formatCurrency(po.total, po.currency, locale)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14815">
                            {po.items}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14816">
                            <button onClick={() => togglePOExpansion(po.id)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-14817">
                              <span data-id="element-14818">Details</span>
                              <ChevronRightIcon className={`ml-1 h-5 w-5 transform transition-transform ${expandedPO === po.id ? 'rotate-90' : ''}`} data-id="element-14819" />
                            </button>
                          </td>
                        </tr>
                        {expandedPO === po.id && <tr data-id="element-14820">
                            <td colSpan={8} className="px-6 py-4 bg-gray-50" data-id="element-14821">
                              <div className="border rounded-md p-4 bg-white" data-id="element-14822">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-14823">
                                  <div data-id="element-14824">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-14825">
                                      Entity / Site
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-14826">
                                      {po.entity} - {po.site}
                                    </p>
                                  </div>
                                  <div data-id="element-14827">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-14828">
                                      Approver
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-14829">
                                      {po.approver} (
                                      {new Date(po.approvalDate).toLocaleDateString(locale)}
                                      )
                                    </p>
                                  </div>
                                  <div data-id="element-14830">
                                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-14831">
                                      Expected Delivery
                                    </h4>
                                    <p className="text-sm text-gray-900" data-id="element-14832">
                                      {new Date(po.expectedDelivery).toLocaleDateString(locale)}
                                    </p>
                                  </div>
                                </div>
                                {po.status === 'Cancelled' && <div className="mb-4 p-3 bg-red-50 rounded-md" data-id="element-14833">
                                    <h4 className="text-xs font-medium text-red-800 uppercase tracking-wider mb-1" data-id="element-14834">
                                      Cancellation Details
                                    </h4>
                                    <p className="text-sm text-red-800" data-id="element-14835">
                                      Cancelled by {po.cancelledBy} on{' '}
                                      {new Date(po.cancellationDate).toLocaleDateString(locale)}
                                    </p>
                                    <p className="text-sm text-red-800" data-id="element-14836">
                                      Reason: {po.cancellationReason}
                                    </p>
                                  </div>}
                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14837">
                                  Line Items
                                </h4>
                                <div className="overflow-x-auto" data-id="element-14838">
                                  <table className="min-w-full divide-y divide-gray-200" data-id="element-14839">
                                    <thead className="bg-gray-50" data-id="element-14840">
                                      <tr data-id="element-14841">
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14842">
                                          Item ID
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14843">
                                          Description
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14844">
                                          Quantity
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14845">
                                          Unit Price
                                        </th>
                                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14846">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-14847">
                                      {po.lineItems.map(item => <tr key={item.id} data-id="element-14848">
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-14849">
                                            {item.id}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900" data-id="element-14850">
                                            {item.description}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-14851">
                                            {item.quantity}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500" data-id="element-14852">
                                            {formatCurrency(item.unitPrice, po.currency, locale)}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-14853">
                                            {formatCurrency(item.total, po.currency, locale)}
                                          </td>
                                        </tr>)}
                                    </tbody>
                                    <tfoot data-id="element-14854">
                                      <tr className="bg-gray-50" data-id="element-14855">
                                        <td colSpan={4} className="px-3 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-14856">
                                          Total:
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900" data-id="element-14857">
                                          {formatCurrency(po.total, po.currency, locale)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>)}
                  </tbody>
                </table>
                {sortedPOs.length === 0 && <div className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-14858">
                    No purchase orders found matching the selected filters.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Summary section */}
      <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-14859">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-14860">
          Summary by Status
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-14861">
          {Object.entries(statusCounts).map(([status, count]) => <div key={status} className="bg-white overflow-hidden shadow-sm rounded-lg p-4" data-id="element-14862">
              <div className="text-sm font-medium text-gray-500" data-id="element-14863">{status}</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-14864">
                {count}
              </div>
              <div className="mt-1 text-xs text-gray-500" data-id="element-14865">
                {Math.round(count / totalPOs * 100)}% of total
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}